import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined';
import Styles from './ClientDataTable.module.scss';

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

/**
 * Floating cue on the page start edge (right in RTL, left in LTR).
 * Moves down as the user scrolls toward Notes, and back up when scrolling up.
 * Hidden once Notes is in view.
 */
const NotesAttentionCue = ({ targetRef, label, direction = 'rtl' }) => {
  const isRtl = direction !== 'rtl';
  const [visible, setVisible] = useState(false);
  const [y, setY] = useState(72);
  const clickedAwayRef = useRef(false);
  const maxDistRef = useRef(0);
  const rafRef = useRef(0);

  useEffect(() => {
    const target = targetRef?.current;
    if (!target) return undefined;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) return undefined;

    const update = () => {
      if (clickedAwayRef.current) {
        setVisible(false);
        return;
      }

      const rect = target.getBoundingClientRect();
      const vh = window.innerHeight || 0;
      const topPad = 72;
      const bottomPad = 100;
      const maxY = vh - bottomPad;

      // Notes already on screen → hide cue.
      if (rect.top < vh * 0.78 && rect.bottom > 80) {
        setVisible(false);
        return;
      }

      // Distance left until Notes reaches mid-viewport.
      const dist = Math.max(0, rect.top - vh * 0.45);
      if (dist > maxDistRef.current) {
        maxDistRef.current = dist;
      }
      const journey = Math.max(maxDistRef.current, 1);
      // 0 at page top → 1 as Notes approaches → cue travels top → down.
      const progress = 1 - clamp(dist / journey, 0, 1);
      const nextY = topPad + (maxY - topPad) * progress;

      setY(nextY);
      setVisible(true);
    };

    const scheduleUpdate = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(update);
    };

    maxDistRef.current = 0;
    const startTimer = window.setTimeout(scheduleUpdate, 400);

    window.addEventListener('scroll', scheduleUpdate, true);
    window.addEventListener('resize', scheduleUpdate);

    return () => {
      window.clearTimeout(startTimer);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      window.removeEventListener('scroll', scheduleUpdate, true);
      window.removeEventListener('resize', scheduleUpdate);
    };
  }, [targetRef]);

  const scrollToNotes = () => {
    const target = targetRef?.current;
    if (!target) return;
    clickedAwayRef.current = true;
    setVisible(false);
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    target.classList.add(Styles.notesPanelSpotlight);
    window.setTimeout(() => {
      target.classList.remove(Styles.notesPanelSpotlight);
    }, 1600);
  };

  if (typeof document === 'undefined') return null;

  return createPortal(
    <AnimatePresence>
      {visible && (
        <motion.button
          type="button"
          key="notes-attention-cue"
          className={`${Styles.notesCue} ${isRtl ? Styles.notesCueRtl : Styles.notesCueLtr}`}
          aria-label={label}
          onClick={scrollToNotes}
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, y, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9, transition: { duration: 0.2 } }}
          transition={{ type: 'spring', stiffness: 260, damping: 32, mass: 0.65 }}
        >
          <span className={Styles.notesCueTrail} aria-hidden="true" />
          <span className={Styles.notesCueBadge}>
            <StickyNote2OutlinedIcon className={Styles.notesCueBadgeIcon} />
            <span className={Styles.notesCueLabel}>{label}</span>
          </span>
          <motion.span
            className={Styles.notesCueArrow}
            animate={{ y: [0, 7, 0] }}
            transition={{ duration: 0.85, repeat: Infinity, ease: 'easeInOut' }}
          >
            <KeyboardArrowDownRoundedIcon fontSize="large" />
          </motion.span>
        </motion.button>
      )}
    </AnimatePresence>,
    document.body,
  );
};

export default NotesAttentionCue;
